import React from 'react';
import { Text, Box, clsx, Flex } from '@mantine/core';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from 'react-responsive';
import { ResizingMode, type EntriesDetails } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { PageHeaderContent } from '~/domains/common/components/PageHeader/PageHeader';
import { Link } from '~/domains/common/components/Link/Link';
import { Routes } from '~/domains/common/constants/routes';
import dt from '~/testing';
import { BadgeGuaranteed } from '~/components/shared/Badges/Badges';

import { getDeadlineFormat } from '../../utils/deadlineFormat';
import getContestMaxEntriesLabel from '../../../gamecenter/domains/common/utils/getContestMaxEntriesLabel';
import type { Contest } from '../../../services/interfaces/contest.interface';

import useStyles from './ContestHeader.styles';

type ContestHeaderContentProps = {
  deadline: Date;
  entries: EntriesDetails;
  fee: number;
  prize: number;
  username: string;
  ownerId: string;
  createdByAdmin: boolean;
  isSidePanel?: boolean;
  resizingMode?: ResizingMode;
  isEntertainmentOnly?: boolean;
};

export function ContestHeaderContent({
  isEntertainmentOnly,
  entries,
  fee,
  prize,
  deadline,
  username,
  ownerId,
  createdByAdmin,
  isSidePanel,
  resizingMode,
}: ContestHeaderContentProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.xs });
  const { t } = useTranslation('contest');

  const { classes } = useStyles({ isSidePanel });
  const maxEntries = getContestMaxEntriesLabel(entries);

  const contestInfo = [
    {
      id: 0,
      label: 'Creator',
      value: (
        <Text component="span" display="block">
          {username ? (
            <Link href={Routes.commissionerChannel(ownerId)} passHref>
              {createdByAdmin ? t('detail.about.info.adminCreator') : username}
            </Link>
          ) : (
            t('detail.about.info.adminCreator')
          )}
        </Text>
      ),
      isHiddenOnMobile: isMobile,
    },
    !isEntertainmentOnly && {
      id: 1,
      label: (
        <Flex align="center" gap={2}>
          {t('detail.header.info.prize')}
          {resizingMode === ResizingMode.GUARANTEED ? <BadgeGuaranteed iconSize={16} /> : null}
        </Flex>
      ),
      value: CurrencyConvertor(prize / 100),
      isPrimary: true,
    },
    !isEntertainmentOnly && {
      id: 2,
      label: t('detail.header.info.entry'),
      value: CurrencyConvertor(fee / 100),
    },
    {
      id: 3,
      label: t('detail.header.info.entries'),
      value: `${entries.filled} / ${maxEntries}`,
      isHiddenOnMobile: isMobile || isSidePanel,
    },
    {
      id: 4,
      label: t('detail.header.info.deadline'),
      value: (
        <Text color={theme.black} fz={theme.fontSizes.md}>
          {getDeadlineFormat(deadline)}
        </Text>
      ),
      isHiddenOnMobile: isMobile || isSidePanel || new Date(deadline.toString()) <= new Date(),
    },
  ].filter(Boolean);

  return (
    <PageHeaderContent
      data-test-id={dt.contestDetail.page.header.content}
      isSidePanel={isSidePanel}
    >
      {contestInfo.map(
        ({ label, id, value, isPrimary, isHiddenOnMobile }) =>
          !isHiddenOnMobile && (
            <Box className={classes.flex} key={id}>
              <Text
                className={classes.contestInfo}
                color={isPrimary ? theme.colors.cyan[6] : 'inherit'}
                m={0}
                component="h5"
                fw={600}
              >
                {value}
              </Text>
              <Text component="span" className={clsx(classes.dimmedText, classes.contestInfo)}>
                {label}
              </Text>
            </Box>
          )
      )}
    </PageHeaderContent>
  );
}
